import { useState, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Select from "react-select";
import FloatingLabelInput from "./FloatingLabelInput";
import "./Form.css";

gsap.registerPlugin(ScrollTrigger);

export default function Form() {
  const { t } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  // Validation du formulaire
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isFormValid = () => {
    return (
      formData.firstName &&
      formData.lastName &&
      formData.email &&
      isValidEmail(formData.email) &&
      formData.service &&
      formData.service !== "" &&
      formData.message
    );
  };

  // Refs pour les animations
  const containerRef = useRef(null);
  const contactInfoRef = useRef(null);
  const contactItem1Ref = useRef(null);
  const contactItem2Ref = useRef(null);
  const contactSocialRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Animation du conteneur contact-info
      gsap.fromTo(
        contactInfoRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Animation progressive des éléments de contact
      gsap.fromTo(
        contactItem1Ref.current,
        { opacity: 0, x: -50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          delay: 0.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        contactItem2Ref.current,
        { opacity: 0, x: -50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          delay: 0.4,
          ease: "power2.out",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        contactSocialRef.current,
        { opacity: 0, x: -50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          delay: 0.6,
          ease: "power2.out",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  // Options pour le menu déroulant personnalisé
  const serviceOptions = [
    {
      value: "",
      label: t("contact.form.service") || "QUELS SERVICES VOUS INTÉRESSE ?",
    },
    {
      value: "branding",
      label: t("contact.form.serviceBranding") || "BRANDING",
    },
    {
      value: "website",
      label: t("contact.form.serviceWebsite") || "SITE INTERNET",
    },
    {
      value: "it-services",
      label: t("contact.form.serviceIT") || "IT SERVICES",
    },
    {
      value: "social-media",
      label: t("contact.form.serviceSocial") || "SOCIAL MÉDIA",
    },
    {
      value: "not-sure",
      label: t("contact.form.serviceNotSure") || "JE NE SAIS PAS ENCORE",
    },
  ];

  // Styles personnalisés pour react-select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "transparent",
      border: "none",
      borderBottom: "1px solid rgb(255, 255, 255)",
      borderRadius: 0,
      boxShadow: "none",
      color: "white",
      fontSize: "var(--fs-body)",
      fontFamily: "inherit",
      padding: "0.5rem 0",
      cursor: "pointer",
      "&:hover": {
        borderBottomColor: "rgba(255, 255, 255, 0.8)",
      },
      "&:focus": {
        borderBottomColor: "rgba(255, 255, 255, 0.8)",
        boxShadow: "none",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: 0,
    }),
    input: (provided) => ({
      ...provided,
      color: "white",
      fontSize: "var(--fs-body)",
      fontFamily: "inherit",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "rgba(255, 255, 255, 0.5)",
      fontSize: "var(--fs-body)",
      fontFamily: "inherit",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "white",
      fontSize: "var(--fs-body)",
      fontFamily: "inherit",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "rgba(0, 0, 0, 0.95)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "4px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "rgba(255, 255, 255, 0.2)"
        : state.isFocused
        ? "rgba(255, 255, 255, 0.1)"
        : "transparent",
      color: "white",
      fontSize: "var(--fs-body)",
      fontFamily: "inherit",
      padding: "0.75rem 1rem",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.15)",
      },
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "white",
      "&:hover": {
        color: "rgba(255, 255, 255, 0.8)",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage("");

    try {
      // Validation des champs requis
      if (
        !formData.firstName ||
        !formData.lastName ||
        !formData.email ||
        !formData.service ||
        !formData.message
      ) {
        setSubmitMessage(
          t("contact.form.error") || "Veuillez remplir tous les champs requis."
        );
        setIsSubmitting(false);
        return;
      }

      // Validation email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setSubmitMessage(
          t("contact.form.errorEmail") ||
            "Veuillez entrer une adresse email valide."
        );
        setIsSubmitting(false);
        return;
      }

      // Obtenir le token reCAPTCHA
      let recaptchaToken = null;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha("contact_form");
        } catch (recaptchaError) {
          console.error("Erreur reCAPTCHA:", recaptchaError);
          // Continuer même si reCAPTCHA échoue (pour le développement)
          // En production, vous pourriez vouloir bloquer la soumission
        }
      }

      // Préparer les données à envoyer
      const dataToSend = {
        ...formData,
        recaptchaToken: recaptchaToken,
      };

      // Envoyer au serveur PHP
      const response = await fetch("/contact-form.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataToSend),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitMessage(
          t("contact.form.success") ||
            result.message ||
            "Message sent successfully!"
        );
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          service: "",
          message: "",
        });
      } else {
        setSubmitMessage(
          result.message ||
            t("contact.form.error") ||
            "Erreur lors de l'envoi du message."
        );
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      setSubmitMessage(
        t("contact.form.error") || "Erreur lors de l'envoi du message."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-section" ref={containerRef}>
      <h1 className="contact-header">
        {t("contact.header") || "CONTACTEZ-NOUS"}
      </h1>

      <div className="contact-content">
        {/* Section gauche - Informations de contact */}
        <div className="contact-info" ref={contactInfoRef}>
          <div className="contact-info-item" ref={contactItem1Ref}>
            <a href="tel:+33 04 93 41 05 08" className="contact-phone">
              04 93 41 05 08
            </a>
            <a href="mailto:contact@maelcorp.com" className="contact-email">
              CONTACT@MAELCORP.COM
            </a>
          </div>

          <div className="contact-info-item" ref={contactItem2Ref}>
            <a
              href="https://www.google.com/maps/place/460+Chemin+du+Col+de+l'Olivier,+06670+Saint-Blaise,+France"
              className="contact-address"
              target="_blank"
              rel="noopener noreferrer"
            >
              460 CHEMIN DU COL DE L'OLIVIER
              <br />
              06670 SAINT BLAISE
            </a>
          </div>

          <div className="contact-social" ref={contactSocialRef}>
            <a
              href="https://www.instagram.com/maelcorporation/"
              className="social-link instagram"
              aria-label="Instagram"
            >
              [IG]
            </a>
            <a
              href="https://fr.linkedin.com/company/maelcorp"
              className="social-link linkedin"
              aria-label="LinkedIn"
            >
              [LI]
            </a>
          </div>
        </div>

        {/* Section droite - Formulaire */}
        <div className="contact-form">
          <form onSubmit={handleSubmit} className="contact-form-container">
            <div className="form-row">
              <div className="form-group">
                <FloatingLabelInput
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  label={t("contact.form.firstName") || "PRÉNOM"}
                />
              </div>

              <div className="form-group">
                <FloatingLabelInput
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  label={t("contact.form.lastName") || "NOM"}
                />
              </div>
            </div>

            <div className="form-group">
              <Select
                id="service"
                name="service"
                value={serviceOptions.find(
                  (option) => option.value === formData.service
                )}
                onChange={(selectedOption) => {
                  setFormData((prev) => ({
                    ...prev,
                    service: selectedOption ? selectedOption.value : "",
                  }));
                }}
                options={serviceOptions}
                styles={customStyles}
                isSearchable={false}
                required
                className="custom-select"
                classNamePrefix="custom-select"
              />
            </div>
            <div className="form-group">
              <FloatingLabelInput
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                label={t("contact.form.email") || "E-MAIL"}
              />
            </div>
            <div className="form-group">
              <FloatingLabelInput
                type="textarea"
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                label={t("contact.form.message") || "MESSAGE"}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !isFormValid()}
              className="form-submit-btn websiteBtn"
            >
              {isSubmitting
                ? t("contact.form.sending") || "Envoi en cours..."
                : t("contact.form.send") || "Envoyer"}
            </button>

            {submitMessage && (
              <div
                className={`form-message ${
                  submitMessage.includes("Erreur") ||
                  submitMessage.includes("Error") ||
                  submitMessage.includes("error")
                    ? "error"
                    : "success"
                }`}
              >
                {submitMessage}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
